import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import type { ApiClient } from '../api';
import type { Me } from '../types';

/**
 * Phone alerts (Matthew, 8 October 2026: "up to the second with what's
 * happening across the ecosystem with notifications to our phones").
 *
 * The bell in the header shows this device's state and opens the alerts
 * panel (2 November 2026, after an iPhone that never got an alert): turn
 * alerts on, send a test line to THIS device and see what happened, and,
 * for an owner, every device getting alerts with when it last checked in
 * and a Remove for stale ones. Same panel in the iPhone app and the
 * website, as Matthew wants the two to match.
 *
 * In a browser, turning on asks for permission and hands the browser's
 * push address to /api/push/subscribe. Inside the A.C.C.R.A iPhone app it
 * is native push: the app asks on first open, hands its APNs device token
 * to /api/push/native on every open, and has no off switch (that is the
 * phone's Settings). When a device cannot turn alerts on, it says why here
 * and reports the reason to /api/push/diagnostic, so it is never silent.
 * Owners and operators only, as the API enforces. Nothing is stored in the
 * browser: the panel asks the server each time it opens.
 */

type State = 'checking' | 'unsupported' | 'install-first' | 'blocked' | 'off' | 'on' | 'busy' | 'unavailable' | 'error';

export function isIos(ua: string): boolean {
  return /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && typeof navigator !== 'undefined' && navigator.maxTouchPoints > 1);
}

/** Whether this page is running inside the A.C.C.R.A iPhone app. */
export const inApp = (): boolean => Capacitor.isNativePlatform();

function standalone(): boolean {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

/** The VAPID public key as the bytes pushManager.subscribe wants. */
export function keyBytes(base64url: string): Uint8Array {
  const b64 = base64url.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(base64url.length / 4) * 4, '=');
  const raw = atob(b64);
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

/** The address the server files an iPhone under (shell/api/src/push.ts apnsEndpoint). */
export const apnsEndpoint = (token: string) => `apns:production:${token.toLowerCase()}`;

/** The server's id for a device: sha256 of its address, so the list can mark this one without sending it. */
async function deviceId(endpoint: string): Promise<string | null> {
  try {
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(endpoint));
    return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
  } catch {
    return null;
  }
}

export interface TestResult {
  sent: number;
  failed: number;
  removed: number;
  skipped: number;
  reasons: string[];
}

/** A test line's outcome, in words. */
export function testOutcome(r: TestResult): { ok: boolean; text: string } {
  if (r.sent > 0) return { ok: true, text: 'Sent ✅ This device should buzz within a few seconds.' };
  const code = r.reasons[0] ?? '';
  if (code === 'not-registered') return { ok: false, text: "This device isn't signed up for alerts yet. Turn them on first." };
  if (code === 'apns-not-configured')
    return { ok: false, text: "iPhone alerts need the Apple push key on the server (AWS secret addaiaspora-shell/apns). It isn't readable yet." };
  if (/^apns-(BadDeviceToken|DeviceTokenNotForTopic|Unregistered|410)$/.test(code))
    return { ok: false, text: "Apple doesn't recognise this iPhone any more. Close the app fully and open it again." };
  if (/^apns-(InvalidProviderToken|ExpiredProviderToken|TopicDisallowed|MissingProviderToken|403)$/.test(code))
    return { ok: false, text: 'Apple refused the push key. Check the APNs key and Team ID in AWS secret addaiaspora-shell/apns.' };
  if (code === 'web-not-configured') return { ok: false, text: "Alerts aren't set up on the server yet." };
  if (/^web-(404|410)$/.test(code)) return { ok: false, text: 'This browser dropped its alerts. Turn them on again.' };
  if (r.skipped > 0) return { ok: false, text: 'Not sent: this kind of device has no push key on the server yet.' };
  return { ok: false, text: `The push service didn't accept it${code ? ` (${code})` : ''}. Try again in a minute.` };
}

export function sinceLabel(iso: string, now = Date.now()): string {
  const mins = Math.max(0, Math.round((now - new Date(iso).getTime()) / 60_000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  if (mins < 48 * 60) return `${Math.round(mins / 60)} h ago`;
  return `${Math.round(mins / 1440)} days ago`;
}

interface Device {
  id: string;
  kind: string;
  who: string;
  mine: boolean;
  updatedAt: string;
  stale: boolean;
}

const LABEL: Record<State, string> = {
  checking: '🔔 Alerts',
  unsupported: '🔕 No alerts here',
  'install-first': '📲 Share → Add to Home Screen for alerts',
  blocked: '🔕 Alerts blocked',
  off: '🔔 Turn on phone alerts',
  on: '🔔 Alerts on',
  busy: '🔔 …',
  unavailable: '🔕 Alerts not set up',
  error: '⚠️ Alerts not working',
};

export function PhoneAlerts({ api, me }: { api: ApiClient; me: Me }) {
  const [state, setState] = useState<State>('checking');
  const [why, setWhy] = useState<string | null>(null);
  const [endpoint, setEndpoint] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);
  const [testing, setTesting] = useState(false);
  const [devices, setDevices] = useState<Device[] | null>(null);
  const [thisId, setThisId] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const allowed = me.role === 'owner' || me.role === 'operator';
  const owner = me.role === 'owner';
  const native = inApp();

  const report = useCallback(
    (stage: string, detail: string) => {
      void api
        .post('/api/push/diagnostic', { stage, detail: detail.slice(0, 200), platform: native ? 'ios-app' : 'web' })
        .catch(() => undefined);
    },
    [api, native],
  );

  const fail = useCallback(
    (stage: string, detail: string) => {
      setState(/501/.test(detail) ? 'unavailable' : 'error');
      setWhy(detail);
      report(stage, detail);
    },
    [report],
  );

  // Inside the iPhone app: native push.
  useEffect(() => {
    if (!allowed || !native) return;
    let live = true;
    const handles = [
      PushNotifications.addListener('registration', ({ value }) => {
        if (!live) return;
        const ep = apnsEndpoint(value);
        setEndpoint(ep);
        void api
          .post('/api/push/native', { token: value, environment: 'production' })
          .then(() => {
            if (!live) return;
            setState('on');
            setWhy(null);
          })
          .catch((err: Error) => live && fail('save', `Saving this iPhone failed: ${err.message}`));
      }),
      PushNotifications.addListener('registrationError', ({ error }) => {
        if (live) fail('register', `Apple wouldn't register this iPhone: ${error}`);
      }),
    ];
    void (async () => {
      let perm = await PushNotifications.checkPermissions();
      if (perm.receive === 'prompt' || perm.receive === 'prompt-with-rationale') {
        perm = await PushNotifications.requestPermissions();
      }
      if (!live) return;
      if (perm.receive !== 'granted') {
        setState(perm.receive === 'denied' ? 'blocked' : 'off');
        if (perm.receive === 'denied') report('permission', 'notifications denied in iOS settings');
        return;
      }
      await PushNotifications.register();
    })().catch((err: Error) => live && fail('register', `Alerts couldn't start: ${err.message}`));
    return () => {
      live = false;
      for (const h of handles) void h.then((x) => x.remove());
    };
  }, [api, allowed, native, fail, report]);

  // In a browser: web push.
  useEffect(() => {
    if (!allowed || native) return;
    if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
      setState(isIos(navigator.userAgent) && !standalone() ? 'install-first' : 'unsupported');
      return;
    }
    if (Notification.permission === 'denied') {
      setState('blocked');
      return;
    }
    let live = true;
    void navigator.serviceWorker.ready
      .then((reg) => reg.pushManager.getSubscription())
      .then(async (sub) => {
        if (!live) return;
        if (sub && Notification.permission === 'granted') {
          setState('on');
          setEndpoint(sub.endpoint);
          // Refresh: keeps the stored role and brands current.
          await api.post('/api/push/subscribe', sub.toJSON()).catch(() => undefined);
        } else {
          setState('off');
        }
      })
      .catch(() => live && setState('unsupported'));
    return () => {
      live = false;
    };
  }, [api, allowed, native]);

  useEffect(() => {
    if (!endpoint) return;
    void deviceId(endpoint).then(setThisId);
  }, [endpoint]);

  const loadDevices = useCallback(() => {
    if (!owner) return;
    void api
      .get<{ devices: Device[] }>('/api/push/devices')
      .then((d) => setDevices(d.devices))
      .catch(() => setDevices([]));
  }, [api, owner]);

  if (!allowed) return null;

  const turnOn = async () => {
    setState('busy');
    setResult(null);
    if (native) {
      const perm = await PushNotifications.requestPermissions().catch(() => ({ receive: 'denied' as const }));
      if (perm.receive === 'granted') {
        await PushNotifications.register().catch((err: Error) => fail('register', `Alerts couldn't start: ${err.message}`));
      } else {
        setState('blocked');
      }
      return;
    }
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setState(permission === 'denied' ? 'blocked' : 'off');
        return;
      }
      const { publicKey } = await api.get<{ publicKey: string }>('/api/push/key');
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: keyBytes(publicKey) as BufferSource,
      });
      await api.post('/api/push/subscribe', sub.toJSON());
      setEndpoint(sub.endpoint);
      setState('on');
      setWhy(null);
      loadDevices();
    } catch (err) {
      fail('web-subscribe', String((err as Error).message));
    }
  };

  const turnOffHere = async () => {
    setState('busy');
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await api.post('/api/push/unsubscribe', { endpoint: sub.endpoint }).catch(() => undefined);
        await sub.unsubscribe();
      }
      setEndpoint(null);
    } finally {
      setState('off');
      loadDevices();
    }
  };

  const sendTest = async () => {
    setTesting(true);
    setResult(null);
    try {
      const r = await api.post<TestResult>('/api/push/test', endpoint ? { endpoint } : {});
      setResult(testOutcome(r));
    } catch (err) {
      setResult({ ok: false, text: `Couldn't reach the server: ${(err as Error).message}` });
    } finally {
      setTesting(false);
      loadDevices();
    }
  };

  const remove = async (id: string) => {
    setConfirming(null);
    await api.post(`/api/push/devices/${id}/remove`, {}).catch(() => undefined);
    loadDevices();
  };

  const show = () => {
    setOpen(true);
    setResult(null);
    loadDevices();
  };

  const statusLine: Record<State, string> = {
    checking: 'Checking…',
    busy: 'Working…',
    on: native ? 'On for this iPhone.' : 'On for this browser.',
    off: native ? 'Off on this iPhone.' : 'Off on this browser.',
    blocked: native
      ? 'Notifications are off for A.C.C.R.A. Turn them on in iPhone Settings → Notifications → A.C.C.R.A.'
      : 'Notifications are blocked for this site. Allow them in the browser or phone settings, then reload.',
    unsupported: "This browser can't receive push alerts.",
    'install-first': 'On iPhone: tap Share, then "Add to Home Screen", open A.C.C.R.A from there and turn alerts on. Or use the A.C.C.R.A app.',
    unavailable: 'Phone alerts are not configured on the server yet.',
    error: why ?? 'Alerts could not start on this device.',
  };

  return (
    <>
      <button
        type="button"
        className={`cc-alerts ${state}`}
        onClick={show}
        aria-haspopup="dialog"
        title={statusLine[state]}
        aria-label={`Phone alerts: ${LABEL[state].slice(2).trim()}`}
      >
        {/* On a phone the header keeps the bell alone (theme.css hides the words). */}
        <span className="cc-alerts-icon" aria-hidden>
          {LABEL[state].slice(0, 2)}
        </span>
        <span className="cc-alerts-words">{LABEL[state].slice(2)}</span>
      </button>
      {open &&
        createPortal(
          <div className="cc-sheet-backdrop" onClick={() => setOpen(false)}>
            <section className="cc-sheet cc-alerts-sheet" role="dialog" aria-label="Phone alerts" onClick={(e) => e.stopPropagation()}>
              <header>
                <h3>🔔 Phone alerts</h3>
                <button type="button" onClick={() => setOpen(false)}>
                  Close
                </button>
              </header>

              <p className={`cc-alerts-status ${state}`}>{statusLine[state]}</p>

              {state === 'on' ? (
                <button type="button" className="cc-sheet-primary" onClick={sendTest} disabled={testing}>
                  {testing ? 'Sending…' : 'Send test alert to this device'}
                </button>
              ) : state === 'off' || state === 'error' || (state === 'blocked' && native) ? (
                <button type="button" className="cc-sheet-primary" onClick={turnOn}>
                  {state === 'error' ? 'Try again' : 'Turn on alerts'}
                </button>
              ) : null}
              {result && (
                <p className={`cc-alerts-result ${result.ok ? 'ok' : 'bad'}`} role="status">
                  {result.text}
                </p>
              )}
              {state === 'on' && !native && (
                <button type="button" className="cc-alerts-link" onClick={turnOffHere}>
                  Turn off on this browser
                </button>
              )}

              {owner && (
                <div className="cc-alerts-devices">
                  <h4>Devices getting alerts</h4>
                  {devices === null ? (
                    <p className="cc-sheet-note">Loading…</p>
                  ) : devices.length === 0 ? (
                    <p className="cc-sheet-note">No devices yet.</p>
                  ) : (
                    <ul>
                      {devices.map((d) => (
                        <li key={d.id} className={d.stale ? 'stale' : undefined}>
                          <span className="cc-alerts-device">
                            <strong>
                              {d.kind}
                              {d.id === thisId ? ' · this device' : ''}
                            </strong>
                            <small>
                              {d.who} · checked in {sinceLabel(d.updatedAt)}
                              {d.stale ? ' · not sent to (over 30 days)' : ''}
                            </small>
                          </span>
                          {confirming === d.id ? (
                            <span className="cc-alerts-confirm">
                              <button type="button" onClick={() => void remove(d.id)}>
                                Remove
                              </button>
                              <button type="button" onClick={() => setConfirming(null)}>
                                Keep
                              </button>
                            </span>
                          ) : (
                            <button type="button" className="cc-alerts-link" onClick={() => setConfirming(d.id)}>
                              Remove
                            </button>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                  <p className="cc-sheet-note">
                    A device checks in each time A.C.C.R.A opens on it. Remove ones you no longer use; a removed device signs
                    itself up again the next time it opens with alerts allowed.
                  </p>
                </div>
              )}
            </section>
          </div>,
          document.body,
        )}
    </>
  );
}
